import type { CustomerCart, CustomerCartStatus, SessionContext } from "../../model/CustomerCartTypes";
import { ServiceError } from "../BaseTypes";
import { CartEventPayload, ItemAddedPayload, ItemQuantitySetPayload, ItemRemovedPayload } from "./CustomerCartEvents";
import type { ICartEventRepository } from "../common-cart/CartEventRepository";
import type { CustomerCartWebsocketParams, ICartWebsocketConnector } from "../common-cart/CartWebsocketConnector";
import type { CartContextStoreImpl } from "../common-cart/CartContextStore";
import type { CustomerDataStoreImpl } from "../CustomerDataStore";
import { LazyStore } from "../../utils/Store";
import { TrackingActions } from "../../actions/TrackingActions";
import { trackUiError } from "../../utils/TrackingUtil";
import { SequentialRequestProcessor } from "../../server/SequentialRequestProcessor";
import { SessionStore } from "../../uidispatcherapi/SessionStore";
import * as CustomerCartServerRequests from "../../server/CustomerCartServerRequests";
import { generateRequestId } from "../common-cart/CommonCartStore";
import { ErrorHandler, checkoutErrorMessageHandlers, cartErrorMessageHandlers } from "../ErrorHandler";
import { isEventPending, parseServerEvent, CustomerCartEvent } from "./CustomerCartEvents";
import deepEquals from "fast-deep-equal/es6";
import {
	emitCartEventFailureEvent,
	emitCartFetchedFreshEvent,
	emitCartRequestFailureEvent,
	emitItemQuantityChangedEvent,
} from "../../events/CartEvents";

const POLLING_TIME = 750;
const PENDING_EVENT_TIMEOUT_MS = 3000;
export type CartStoreState = {
	currentCartId: string | null | undefined;
	confirmedCartState: CustomerCart | null | undefined;
	events: Array<CustomerCartEvent>;
	isPending: boolean;
};
export class EventSourcedCartStoreImpl extends LazyStore {
	_state!: CartStoreState;
	_storeId: string;
	_cartStatus: CustomerCartStatus;
	_sessionContext: SessionContext | null | undefined;
	_cartContextStore: CartContextStoreImpl;
	_customerDataStore: CustomerDataStoreImpl;
	_eventRepository: ICartEventRepository<CustomerCartEvent>;
	_wsConnector: ICartWebsocketConnector<CustomerCartWebsocketParams, CustomerCartEvent>;
	_requestProcessor: SequentialRequestProcessor;
	_resetTimeoutId: any;
	_errorHandler = new ErrorHandler([
		{
			canHandle: (error) => error.code === "OC-104",
			handle: () => {
				this._cartContextStore.resetState();
			},
		}, // DRSM-5428, DRSM-5421, DRSM-5411 report an issue with adding articles into the cart.
		// The logs contain unauthorized error that are also thrown when the user tries to add articles.
		// It's very weird. As a possible mitigation, we should try resetting the state.
		{
			canHandle: (error) => error.code === "OC-941",
			handle: () => {
				// If this does not resolve the problem, we don't want to spam requests.
				this._resetTimeoutId = this._resetTimeoutId || setTimeout(this.resetState, 1000);
			},
		},
		{
			canHandle: (error) => error.code === "OC-998" && error.message === "403",
			handle: (error) => {
				console.warn("Caught Forbidden error.", error);
			},
		},
		...cartErrorMessageHandlers,
		...checkoutErrorMessageHandlers,
	]);

	constructor(
		storeId: string,
		cartStatus: CustomerCartStatus,
		cartContextStore: CartContextStoreImpl,
		customerDataStore: CustomerDataStoreImpl,
		eventRepository: ICartEventRepository<CustomerCartEvent>,
		wsConnector: ICartWebsocketConnector<CustomerCartWebsocketParams, CustomerCartEvent>,
		requestProcessor: SequentialRequestProcessor,
	) {
		super();
		this._storeId = storeId;
		this._cartStatus = cartStatus;
		this._cartContextStore = cartContextStore;
		this._customerDataStore = customerDataStore;
		this._eventRepository = eventRepository;
		this._wsConnector = wsConnector;
		this._requestProcessor = requestProcessor;
		this.resetState();
	}

	onArm = () => {
		this.listenTo(SessionStore, () => {
			this.onContextChange().catch((error) => {
				console.error("onContextChange in EventSourcedCartStore failed with ", error);
			});
		});
		this.listenTo(this._cartContextStore, (...args) => {
			this.onContextChange(...args).catch((error) => {
				console.error("onContextChange in EventSourcedCartStore failed with ", error);
			});
		});
		this.listenTo(this._customerDataStore, (...args) => {
			this.onContextChange(...args).catch((error) => {
				console.error("onContextChange in EventSourcedCartStore failed with ", error);
			});
		});
		this.onContextChange().catch((error) => {
			console.error("onContextChange in EventSourcedCartStore failed with ", error);
		});
	};
	onDisarm = () => {
		this.unhook();
		this.resetState();
	};
	resetState = (): void => {
		this._sessionContext = this.getSessionContext();
		this._resetTimeoutId = null;

		this._state = {
			currentCartId: null,
			confirmedCartState: null,
			events: [],
			isPending: true,
		};

		this._wsConnector.disconnect();

		this.trigger();
	};
	setCartId = (cartId: string): void => {
		this._cartContextStore.resetState(cartId);
	};
	getCartId = (): string | null => this._state.currentCartId || null;

	onContextChange = async (): Promise<void> => {
		const currentCartId = this._state.currentCartId;

		if (SessionStore.notLoggedIn() && currentCartId) {
			this.resetState();
			return;
		}

		const sessionContext = this.getSessionContext();

		const { cartId: targetCartId } = this._cartContextStore.getState();

		if (!deepEquals(sessionContext, this._sessionContext) || currentCartId !== targetCartId) {
			if (!sessionContext || !targetCartId) {
				this.resetState();
				return;
			}

			this._sessionContext = sessionContext;
			this._state = { ...this._state, currentCartId: targetCartId };
			const { customerId, storeId } = sessionContext;

			await this.fetchCart(sessionContext, targetCartId, this._cartStatus);

			this._wsConnector.connect(
				{
					customerId,
					storeId,
					cartId: targetCartId,
				},
				this.onEventConfirmed,
				this.onWebsocketClosed,
			);
		}
	};
	fetchCart = async (sessionContext: SessionContext, cartId: string, status: CustomerCartStatus): Promise<void> => {
		try {
			const confirmedCartState = await CustomerCartServerRequests.getCart(
				{
					sessionContext,
					cartId,
				},
				status,
			);
			if (confirmedCartState.cartId !== this._state.currentCartId) {
				return;
			}

			const events = await this.consolidateEvents(confirmedCartState);
			this._sessionContext = sessionContext;
			this._state = {
				...this._state,
				confirmedCartState,
				events,
				isPending: false,
			};
			emitCartFetchedFreshEvent({ cartId, commonStatus: confirmedCartState.status });
			this.trigger();
		} catch (e) {
			this.handleRequestError(e as any, cartId);
		}
	};
	consolidateEvents = async (cart: CustomerCart): Promise<Array<CustomerCartEvent>> => {
		const localEvents = await this._eventRepository.getEvents(cart.cartId);
		const pendingEvents = localEvents.filter((it) => it.status.type === "Pending");

		const confirmEvent = (event: CustomerCartEvent): CustomerCartEvent =>
			Object.assign(event, {
				_data: {
					...event._data,
					status: {
						type: "Confirmed",
					},
					seqNum: cart.eventHistory.indexOf(event.eventId),
				},
			});

		await Promise.all(
			pendingEvents
				.filter((event) => cart.eventHistory.includes(event.eventId))
				.map((event) => confirmEvent(event))
				.map((event) => this._eventRepository.saveEvent(event.cartId, event)),
		);
		return this._eventRepository.getEvents(cart.cartId);
	};
	getSessionContext = (): SessionContext | null => {
		const { customerId, cardholderNumber, storeId, fsdAddressId, country, locale } =
			this._cartContextStore.getState();

		if (!customerId || !cardholderNumber || !storeId || !country || !locale) {
			return null;
		}

		return this.isValidSessionState(customerId, storeId, fsdAddressId, cardholderNumber)
			? {
					customerId,
					cardholderNumber,
					storeId,
					fsdAddressId,
					country,
					locale,
				}
			: null;
	};
	getOptimisticState = (): CustomerCart | null => {
		return (
			(this._state.confirmedCartState &&
				EventSourcedCartStoreImpl.applyEventsOptimistically(
					this._state.confirmedCartState,
					this.getPendingEvents(),
				)) ||
			null
		);
	};
	getAppliedEvents = (): CustomerCartEvent[] => this._state.events.filter((it) => !isEventPending(it));
	getPendingEvents = (): CustomerCartEvent[] =>
		this._state.events.filter((it) => isEventPending(it) && it.age <= PENDING_EVENT_TIMEOUT_MS);
	isPending = () => this._state.isPending;
	pushEvent = async (event: CustomerCartEvent): Promise<void> => {
		if (isEventPending(event)) {
			await this.handlePendingEvent(event);
		} else {
			this.handleNonPendingEvent(event);
		}

		this.trigger();
		return this._eventRepository.saveEvent(event.cartId, event);
	};

	handlePendingEvent = async (event: CustomerCartEvent): Promise<void> => {
		const sessionContext = this._sessionContext;
		const timeoutId = setTimeout(async () => {
			if (!sessionContext || !deepEquals(sessionContext, this._sessionContext)) return;
			const events = await this.fetchEvents(sessionContext, event.cartId, [event.eventId]);
			const missedEvent = events.find((it) => it.eventId === event.eventId);

			if (missedEvent) {
				await this.onEventConfirmed(missedEvent);
				return;
			}

			// Remove the unconfirmed event from the state.
			this._state = {
				...this._state,
				events: [...this._state.events.filter((it) => it.eventId !== event.eventId)],
			};
			this.trigger();
		}, PENDING_EVENT_TIMEOUT_MS);
		this._state = { ...this._state, events: [...this._state.events, event.withResetTimeoutId(timeoutId)] };
	};

	handleNonPendingEvent = (event: CustomerCartEvent): void => {
		this._state = {
			...this._state,
			events: [
				...this._state.events.filter((it) => {
					if (it.eventId !== event.eventId) return true;
					if (isEventPending(it) && it.resetTimeoutId) clearTimeout(it.resetTimeoutId);
					return false;
				}),
				event,
			],
		};
	};

	onWebsocketClosed = async (wsParams: CustomerCartWebsocketParams): Promise<void> => {
		const { cartId } = wsParams;

		const checkCartVersion = async () => {
			const sessionContext = this._sessionContext;
			const confirmedState = this._state.confirmedCartState;

			if (!sessionContext || !confirmedState || cartId !== confirmedState.cartId) {
				return;
			}

			try {
				const { cartVersion, eventHistory } = await CustomerCartServerRequests.getCartVersion({
					sessionContext,
					cartId,
				});

				if (cartVersion !== confirmedState.cartVersion) {
					const appliedEventIds = confirmedState.eventHistory;
					const missedEventIds = eventHistory.filter((it) => !appliedEventIds.includes(it));
					const missedEvents = await this.fetchEvents(sessionContext, cartId, missedEventIds);
					missedEvents.forEach((it) => this.onEventConfirmed(it));
				}

				setTimeout(checkCartVersion, POLLING_TIME);
			} catch (e) {
				this.handleRequestError(e as any, cartId);
			}
		};

		return checkCartVersion();
	};

	_computeNewState = (confirmedState: CustomerCart, event: CustomerCartEvent): CustomerCart | null => {
		try {
			return event.applyTo(confirmedState);
		} catch (e) {
			trackUiError(
				"EventSourcedCartStore",
				`Tried to apply event ${event.eventId} to cart ${confirmedState.cartId}. Setting new state as null.`,
				e,
			);
			return null;
		}
	};

	_isSuccessfulEventOfPayloadType(event: CustomerCartEvent, payloadType: string): boolean {
		return event.payload.type === payloadType && event.status.type === "Success";
	}

	_emitCustomEvents(
		event: CustomerCartEvent,
		newState: CustomerCart | null,
		confirmedState: CustomerCart | null | undefined,
	): void {
		if (this._isSuccessfulEventOfPayloadType(event, "ItemAddedPayload")) {
			const payload = event.payload as ItemAddedPayload;
			emitItemQuantityChangedEvent({
				cartId: event.cartId,
				commonStatus: newState?.status ?? "ACTIVE",
				eventId: event.eventId,
				bundleId: payload.data.bundleId,
				previousQuantity: confirmedState?.items[payload.data.bundleId]?.quantity ?? 0,
				newQuantity: newState?.items[payload.data.bundleId]?.quantity ?? payload.data.quantity,
			});
		}
		if (this._isSuccessfulEventOfPayloadType(event, "ItemQuantitySetPayload")) {
			const payload = event.payload as ItemQuantitySetPayload;
			emitItemQuantityChangedEvent({
				cartId: event.cartId,
				commonStatus: newState?.status ?? "ACTIVE",
				eventId: event.eventId,
				bundleId: payload.data.bundleId,
				previousQuantity: confirmedState?.items[payload.data.bundleId]?.quantity ?? 0,
				newQuantity: newState?.items[payload.data.bundleId]?.quantity ?? payload.data.quantity,
			});
		}
		if (this._isSuccessfulEventOfPayloadType(event, "ItemRemovedPayload")) {
			const payload = event.payload as ItemRemovedPayload;
			emitItemQuantityChangedEvent({
				cartId: event.cartId,
				commonStatus: newState?.status ?? "ACTIVE",
				eventId: event.eventId,
				bundleId: payload.data.bundleId,
				previousQuantity: confirmedState?.items[payload.data.bundleId]?.quantity ?? 0,
				newQuantity: newState?.items[payload.data.bundleId]?.quantity ?? 0,
			});
		}
		if (event.status.type == "Failure") {
			emitCartEventFailureEvent({
				cartId: event.cartId,
				commonStatus: this._cartStatus,
				eventId: event.eventId,
				reason: event.status.error?.code,
			});
		}
	}

	onEventConfirmed = async (event: CustomerCartEvent): Promise<void> => {
		const sessionContext = this._sessionContext;
		const confirmedState = this._state.confirmedCartState;

		if (!sessionContext || !confirmedState || confirmedState.cartId !== event.cartId) {
			this.resetState();
			return;
		}

		if (this._isSuccessfulEventOfPayloadType(event, "CartRestockedPayload")) {
			TrackingActions.trackCartRestocked(event.cartId, Object.keys(event.payload.data.addedItemData));
		}

		if (this._isSuccessfulEventOfPayloadType(event, "SavedCartLoadedPayload")) {
			TrackingActions.trackSavedCartLoaded(event.cartId, Object.keys(event.payload.data.addedItemData));
		}

		if (
			event.seqNum !== confirmedState.cartVersion + 1 ||
			this._isSuccessfulEventOfPayloadType(event, "CartRestockedPayload") ||
			this._isSuccessfulEventOfPayloadType(event, "SavedCartLoadedPayload") ||
			this._isSuccessfulEventOfPayloadType(event, "ItemBatchAddedPayload") ||
			this._isSuccessfulEventOfPayloadType(event, "DeliveryDateSetPayload")
		) {
			await this.fetchCart(sessionContext, event.cartId, this._cartStatus);
			return;
		}

		if (event.status.error) {
			this._errorHandler.onError(null, event.status.error);
		}

		const newState = this._computeNewState(confirmedState, event);
		this._state = { ...this._state, isPending: false, confirmedCartState: newState };

		if (!newState) {
			this._state.events.forEach((it) => {
				if (it.resetTimeoutId) clearTimeout(it.resetTimeoutId);
			});

			this._cartContextStore.handleResetCart(event.cartId);

			return;
		}

		if (!newState.totalPrices) {
			// Retry fetching a cart with prices once.
			await this.fetchCart(sessionContext, event.cartId, this._cartStatus);
		}

		await this.pushEvent(event);
		this._emitCustomEvents(event, newState, confirmedState);
	};
	fetchEvents = async (
		sessionContext: SessionContext,
		cartId: string,
		eventIds: Array<string>,
	): Promise<CustomerCartEvent[]> => {
		if (!eventIds.length) return [];

		try {
			const serverEvents = await CustomerCartServerRequests.getEvents({
				sessionContext,
				cartId,
				eventIds,
			});
			return serverEvents
				.map((it) => parseServerEvent(it))
				.filter((it) => !isEventPending(it))
				.sort((a, b) => a.seqNum - b.seqNum);
		} catch (e) {
			this.handleRequestError(e as any, cartId);
			return [];
		}
	};
	send = async <R>(
		payload: CartEventPayload,
		request: (sessionContext: SessionContext, event: CustomerCartEvent) => Promise<R>,
		awaitConfirmation = false,
		eventIdPrefix?: string,
	): Promise<R | null | undefined> => {
		const event = this.createEvent(payload, undefined, eventIdPrefix);
		const sessionContext = this.getSessionContext();

		if (!event || !sessionContext) {
			return null;
		}

		return this.enqueue(() => request(sessionContext, event), event, awaitConfirmation);
	};
	enqueue = async <R>(
		request: () => Promise<R>,
		event: CustomerCartEvent,
		awaitConfirmation = false,
	): Promise<R | null | undefined> => {
		await this.pushEvent(event);

		if (awaitConfirmation) {
			this._state = { ...this._state, isPending: true };
			this.trigger();
		}

		// @ts-expect-error
		return this._requestProcessor.enqueue(async () => {
			try {
				return await request();
			} catch (e) {
				await this.pushEvent(
					event.withStatus({
						type: "Failure",
						error: new ServiceError({
							message: "Too optimistic",
							code: "OC-198",
						}),
					}),
				);
				this.handleRequestError(e as any, event.cartId);
				return null;
			}
		});
	};

	async execute<T>(request: () => Promise<T>): Promise<T | null> {
		try {
			return await request();
		} catch (e) {
			this.handleRequestError(e as any, null);
			return null;
		}
	}

	createEvent = (payload: CartEventPayload, eventId?: string, eventIdPrefix?: string): CustomerCartEvent | null => {
		const cartId = this._cartContextStore.getState().cartId;

		if (!this._sessionContext || !cartId) {
			return null;
		}

		return new CustomerCartEvent(
			{
				cartId: cartId,
				customerId: this._sessionContext.customerId,
				seqNum: this.getNextSeqNum(),
				status: {
					type: "Pending",
				},
				prices: null,
				eventId: eventId || generateRequestId(eventIdPrefix),
				timestamp: new Date().getTime(),
			},
			payload,
		);
	};
	getNextSeqNum = (): number => {
		const lastEvent = this._state.events.slice(-1)[0];

		return (lastEvent && lastEvent.seqNum + 1) || 0;
	};
	handleRequestError = (e: ServiceError, cartId: string | null): void => {
		this._errorHandler.onError(null, e);
		emitCartRequestFailureEvent({ cartId: cartId ?? "", commonStatus: this._cartStatus });
	};

	isValidSessionState = (
		customerId: string,
		storeId: string,
		fsdAddressId: string | null | undefined,
		cardholderNumber: string,
	): boolean => {
		const customerData = this._customerDataStore.getCustomer();

		if (
			!customerData ||
			customerData.customerId !== customerId ||
			!(!!customerData.cardholderById && cardholderNumber in customerData.cardholderById)
		) {
			return false;
		}

		const deliveryAddressIds = this._customerDataStore
			.getDeliveryAddresses(customerId, storeId)
			.map((it) => it.integrationId);

		const marioStore = !customerData.validForFsd || deliveryAddressIds.length === 0;
		const fsdAddressSelected = !!fsdAddressId && deliveryAddressIds.includes(fsdAddressId);
		const isValidState = marioStore || fsdAddressSelected;

		if (!isValidState && fsdAddressId) {
			console.error(`Invalid FSD address selection [${fsdAddressId}]`);
		}

		return isValidState;
	};

	static applyEventsOptimistically(
		initialState: CustomerCart,
		events: Array<CustomerCartEvent>,
	): CustomerCart | null {
		// @ts-expect-error
		return events.reduce((cart, event) => {
			if (cart) {
				try {
					return event.applyTo(cart);
				} catch (e) {
					trackUiError(
						"EventSourcedCartStore",
						`Tried to apply event ${event.eventId} to cart ${cart.cartId}. Ignoring event.`,
						e,
					);
				}
			}
			return cart;
		}, initialState);
	}
}
